<template>
	<el-drawer
		:visible="visible"
		title="主题设置"
		size="560px"
		direction="rtl"
		custom-class="tcs-drawer"
		:append-to-body="true"
		:wrapper-closable="false"
		@update:visible="!$event && onClose()"
	>
		<div class="tcs-content">
			<div class="tcs-intro">
				<div>
					<h3>让工作台更合心意</h3>
					<p>选择舒适的外观和喜欢的主题色。</p>
				</div>
				<span class="tcs-badge"><i class="el-icon-check"></i> 即时生效</span>
			</div>
			<lock-settings v-if="visible" @locked="onClose" />
			<section class="tcs-block" aria-labelledby="tcs-mode-title">
				<div class="tcs-heading">
					<h4 id="tcs-mode-title">外观模式</h4>
					<span>{{ modeLabel }}模式</span>
				</div>
				<div class="tcs-modes" role="group" aria-label="外观模式">
					<button
						v-for="mode in modeOptions"
						:key="mode.value"
						type="button"
						class="tcs-mode"
						:class="{ 'is-active': form.mode === mode.value }"
						:aria-pressed="form.mode === mode.value"
						@click="pickMode(mode.value)"
					>
						<span class="tcs-mini" :class="'tcs-mini--' + mode.value" aria-hidden="true">
							<span class="tcs-mini__nav"><i></i><i></i><i></i></span>
							<span class="tcs-mini__main"
								><i></i><span><b></b><b></b></span><i></i
							></span>
						</span>
						<span class="tcs-mode__label"
							><i :class="mode.icon"></i><b>{{ mode.label }}</b
							><i class="tcs-choice" :class="form.mode === mode.value ? 'el-icon-success' : 'el-icon-circle-check'"></i
						></span>
						<span class="tcs-mode__hint">{{ mode.hint }}</span>
					</button>
				</div>
			</section>
			<section class="tcs-block" aria-labelledby="tcs-color-title">
				<div class="tcs-heading">
					<h4 id="tcs-color-title">主题色</h4>
					<span>{{ colorLabel }} · {{ normalized.toUpperCase() }}</span>
				</div>
				<p class="tcs-help">统一应用于导航、按钮、链接和选中状态</p>
				<div class="tcs-presets" role="group" aria-label="预设主题色">
					<button
						v-for="preset in presets"
						:key="preset.color"
						type="button"
						class="tcs-preset"
						:class="{ 'is-active': normalized === preset.color.toLowerCase() }"
						:aria-pressed="normalized === preset.color.toLowerCase()"
						:aria-label="preset.label + ' ' + preset.color"
						@click="pickColor(preset.color)"
					>
						<span class="tcs-swatch" :style="{ backgroundColor: preset.color }"
							><i v-if="normalized === preset.color.toLowerCase()" class="el-icon-check"></i></span
						><span>{{ preset.label }}</span>
					</button>
				</div>
				<div class="tcs-custom">
					<label class="tcs-picker" :style="{ backgroundColor: normalized }" title="打开自定义取色器">
						<input :value="normalized" type="color" aria-label="自定义主题色" @input="pickColor($event.target.value)" /><i
							class="el-icon-edit"
							aria-hidden="true"
						></i>
					</label>
					<div class="tcs-custom__label"><label for="tcs-hex">自定义颜色</label><span>输入 HEX 色值或点击取色</span></div>
					<input
						id="tcs-hex"
						v-model="hexInput"
						class="tcs-hex"
						:class="{ 'is-invalid': colorError }"
						type="text"
						maxlength="7"
						spellcheck="false"
						autocomplete="off"
						:aria-invalid="!!colorError"
						:aria-describedby="colorError ? 'tcs-color-error' : null"
						@input="colorError = ''"
						@change="applyHex"
						@keydown.enter.prevent="applyHex"
					/>
				</div>
				<p v-if="colorError" id="tcs-color-error" class="tcs-error" role="alert">{{ colorError }}</p>
			</section>
			<section class="tcs-block" aria-labelledby="tcs-preview-title">
				<div class="tcs-heading">
					<h4 id="tcs-preview-title">效果预览</h4>
					<span>跟随当前设置</span>
				</div>
				<div class="tcs-preview" aria-label="工作台主题效果预览">
					<aside class="tcs-preview__nav">
						<strong>六彩视界</strong><span class="is-selected"><i class="el-icon-menu"></i> 工作台</span><span><i class="el-icon-document"></i> 订单管理</span
						><span><i class="el-icon-user"></i> 会员管理</span>
					</aside>
					<div class="tcs-preview__main">
						<div class="tcs-preview__bar"><b>门店工作台</b><span class="tcs-preview__avatar">六</span></div>
						<div class="tcs-preview__content">
							<div class="tcs-preview__summary">
								<div>
									<span>今日订单</span><strong>128 <small>笔</small></strong>
								</div>
								<span class="tcs-preview__button">新建订单</span>
							</div>
							<div class="tcs-preview__row">
								<span><i class="el-icon-circle-check"></i> 已选中订单</span><span class="tcs-preview__tag">处理中</span
								><span class="tcs-preview__link">查看详情</span>
							</div>
						</div>
					</div>
				</div>
			</section>
		</div>
		<footer class="tcs-footer">
			<span class="tcs-save-hint"><i class="el-icon-check"></i> 设置自动保存在当前浏览器</span>
			<div class="tcs-footer__actions">
				<el-button size="small" icon="el-icon-refresh-left" @click="onReset">恢复默认</el-button>
				<el-button type="primary" size="small" @click="onDone">完成</el-button>
			</div>
		</footer>
	</el-drawer>
</template>

<script>
import { getTheme, setThemeMode, setPrimaryColor, resetTheme, DEFAULT_PRIMARY_COLOR } from "@/utils/theme.js";
import LockSettings from "@/components/common/LockSettings.vue";

export default {
	name: "lcsj-theme-config",
	components: { LockSettings },
	props: { visible: { type: Boolean, default: false } },
	data() {
		return {
			presets: [
				{ label: "品牌蓝", color: "#2E5AA8" },
				{ label: "晴空蓝", color: "#1F6FEB" },
				{ label: "松石绿", color: "#0E7C66" },
				{ label: "琥珀金", color: "#B7791F" },
				{ label: "朱砂红", color: "#C53030" },
				{ label: "鸢尾紫", color: "#6B46C1" },
				{ label: "石墨灰", color: "#2D3748" },
				{ label: "雾霭蓝", color: "#52758A" },
				{ label: "湖水青", color: "#267F8C" },
				{ label: "橄榄绿", color: "#6F7A3C" },
				{ label: "陶土橙", color: "#AD6045" },
				{ label: "豆沙玫", color: "#AD5275" },
				{ label: "烟梅紫", color: "#85577D" },
				{ label: "可可棕", color: "#80644D" }
			],
			form: { mode: "light", primaryColor: "" },
			hexInput: DEFAULT_PRIMARY_COLOR,
			colorError: "",
			modeOptions: [
				{ label: "浅色", value: "light", icon: "el-icon-sunny", hint: "清晰明亮，适合日间办公" },
				{ label: "深色", value: "dark", icon: "el-icon-moon", hint: "柔和深色，适合低光环境" }
			]
		};
	},
	computed: {
		normalized() {
			return (this.form.primaryColor || DEFAULT_PRIMARY_COLOR).toLowerCase();
		},
		modeLabel() {
			return this.form.mode === "dark" ? "深色" : "浅色";
		},
		colorLabel() {
			const preset = this.presets.find((item) => item.color.toLowerCase() === this.normalized);
			return preset ? preset.label : "自定义";
		}
	},
	watch: {
		visible: {
			immediate: true,
			handler(open) {
				if (open) this.loadCurrent();
			}
		}
	},
	methods: {
		loadCurrent() {
			this.form = getTheme();
			this.hexInput = this.normalized.toUpperCase();
			this.colorError = "";
		},
		pickMode(mode) {
			setThemeMode(mode);
			this.loadCurrent();
		},
		pickColor(color) {
			setPrimaryColor(color);
			this.loadCurrent();
		},
		applyHex() {
			const value = this.hexInput.trim();
			const color = value.startsWith("#") ? value : "#" + value;
			if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) {
				this.colorError = "请输入 3 位或 6 位十六进制色值，例如 #2E5AA8";
				return false;
			}
			this.pickColor(color);
			return true;
		},
		onReset() {
			resetTheme();
			this.loadCurrent();
			this.$message.success("已恢复默认主题");
		},
		onDone() {
			if (this.hexInput.toLowerCase() !== this.normalized && !this.applyHex()) return;
			this.onClose();
		},
		onClose() {
			this.$emit("update:visible", false);
			this.$emit("close");
		}
	}
};
</script>

<style scoped>
.tcs-content {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	overscroll-behavior: contain;
	padding: 24px;
}
.tcs-footer {
	flex-shrink: 0;
	padding: 16px 24px;
	padding-bottom: calc(16px + env(safe-area-inset-bottom));
	border-top: 1px solid var(--app-border);
	background: var(--app-surface);
}
.tcs-footer__actions {
	display: flex;
	justify-content: space-between;
	margin-top: 12px;
}
.tcs-footer__actions .el-button:last-child {
	min-width: 96px;
}
.tcs-intro,
.tcs-heading,
.tcs-mode__label,
.tcs-custom,
.tcs-preview__bar,
.tcs-preview__summary,
.tcs-preview__row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}
.tcs-intro {
	margin-bottom: 24px;
}
.tcs-intro h3 {
	margin: 0 0 7px;
	font-size: 18px;
	font-weight: 600;
	color: var(--app-text);
}
.tcs-intro p,
.tcs-help {
	margin: 0;
	font-size: var(--app-font-sm);
	color: var(--app-text-secondary);
	line-height: 1.6;
}
.tcs-badge {
	flex-shrink: 0;
	padding: 5px 9px;
	border-radius: var(--app-radius-pill);
	background: var(--app-primary-soft);
	color: var(--app-primary-text);
	font-size: var(--app-font-sm);
}
.tcs-block {
	/* App.vue 的全局 section 高度用于页面布局，设置分区按内容自适应。 */
	height: auto;
}
.tcs-block + .tcs-block {
	margin-top: 18px;
	padding-top: 16px;
	border-top: 1px solid var(--app-border);
}
.tcs-heading {
	margin-bottom: 12px;
}
.tcs-heading h4 {
	margin: 0;
	font-size: var(--app-font-md);
	color: var(--app-text);
	font-weight: 600;
}
.tcs-heading > span {
	font-size: var(--app-font-sm);
	color: var(--app-text-secondary);
}
.tcs-modes {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 14px;
}
.tcs-mode {
	padding: 12px;
	border: 1px solid var(--app-border-strong);
	border-radius: var(--app-radius-md);
	background: var(--app-surface);
	text-align: left;
	cursor: pointer;
	color: var(--app-text-secondary);
	font: inherit;
	transition: border-color 0.18s, box-shadow 0.18s;
}
.tcs-mode:hover,
.tcs-preset:hover {
	border-color: var(--app-primary-text);
}
.tcs-mode.is-active {
	border-color: var(--app-primary-text);
	box-shadow: 0 0 0 1px var(--app-primary-text);
	background: var(--app-primary-soft);
}
.tcs-mode__label {
	justify-content: flex-start;
	gap: 7px;
	margin-top: 12px;
	color: var(--app-text);
}
.tcs-mode__label b {
	font-weight: 600;
}
.tcs-choice {
	margin-left: auto;
	color: var(--app-text-muted);
	font-size: 17px;
}
.is-active .tcs-choice {
	color: var(--app-primary-text);
}
.tcs-mode__hint {
	display: block;
	margin-top: 5px;
	font-size: var(--app-font-sm);
}
.tcs-mini {
	display: flex;
	height: 70px;
	overflow: hidden;
	border: 1px solid var(--app-border);
	border-radius: var(--app-radius-sm);
	background: var(--tcs-mini-page);
}
/* 固定明暗缩略图是模式选项的示意，不跟随当前模式反转。 */
.tcs-mini--light {
	--tcs-mini-page: #f4f6fa;
	--tcs-mini-card: #fff;
	--tcs-mini-line: #d3dae6;
}
.tcs-mini--dark {
	--tcs-mini-page: #131820;
	--tcs-mini-card: #232a36;
	--tcs-mini-line: #4a5568;
}
.tcs-mini__nav {
	width: 26%;
	padding: 12px 8px;
	background: var(--app-sidebar-bg);
}
.tcs-mini__nav i {
	display: block;
	height: 4px;
	margin-bottom: 8px;
	border-radius: 2px;
	background: var(--app-sidebar-text);
	opacity: 0.35;
}
.tcs-mini__nav i:first-child {
	opacity: 1;
	background: var(--app-sidebar-active);
}
.tcs-mini__main {
	flex: 1;
	padding: 10px;
}
.tcs-mini__main > i {
	display: block;
	width: 45%;
	height: 4px;
	background: var(--tcs-mini-line);
	border-radius: 2px;
}
.tcs-mini__main > span {
	display: flex;
	gap: 7px;
	margin: 7px 0;
}
.tcs-mini__main b {
	flex: 1;
	height: 20px;
	border-radius: 3px;
	background: var(--tcs-mini-card);
}
.tcs-help {
	margin-top: -5px;
	margin-bottom: 12px;
}
.tcs-presets {
	display: grid;
	grid-template-columns: repeat(7, minmax(0, 1fr));
	gap: 5px;
}
.tcs-preset {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 9px 3px;
	border: 1px solid transparent;
	border-radius: var(--app-radius);
	color: var(--app-text-secondary);
	background: transparent;
	font: inherit;
	font-size: var(--app-font-sm);
	cursor: pointer;
}
.tcs-preset.is-active {
	background: var(--app-primary-soft);
	border-color: var(--app-primary-border);
	color: var(--app-primary-text);
}
.tcs-swatch {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	color: #fff;
	font-size: 17px;
}
.tcs-custom {
	justify-content: flex-start;
	margin-top: 14px;
	padding: 10px 12px;
	background: var(--app-surface-muted);
	border: 1px solid var(--app-border);
	border-radius: var(--app-radius);
}
.tcs-picker {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 34px;
	height: 34px;
	flex-shrink: 0;
	border: 1px solid var(--app-border-strong);
	border-radius: var(--app-radius-sm);
	color: var(--app-primary-contrast);
	overflow: hidden;
}
.tcs-picker input {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	opacity: 0;
	cursor: pointer;
}
.tcs-custom__label {
	display: flex;
	flex-direction: column;
	gap: 4px;
	color: var(--app-text);
	font-size: var(--app-font-base);
}
.tcs-custom__label span {
	font-size: var(--app-font-sm);
	color: var(--app-text-secondary);
}
.tcs-hex {
	width: 100px;
	min-width: 0;
	margin-left: auto;
	padding: 8px 10px;
	border: 1px solid var(--app-border-strong);
	border-radius: var(--app-radius-sm);
	background: var(--app-surface);
	color: var(--app-text);
	font-family: monospace;
	font-size: var(--app-font-base);
}
.tcs-hex.is-invalid {
	border-color: var(--app-danger);
}
.tcs-error {
	margin: 7px 0 0;
	color: var(--app-danger-text);
	font-size: var(--app-font-sm);
}
.tcs-mode:focus-visible,
.tcs-preset:focus-visible,
.tcs-hex:focus-visible,
.tcs-picker:focus-within {
	outline: 2px solid var(--app-primary-text);
	outline-offset: 3px;
}
.tcs-preview {
	display: flex;
	min-width: 0;
	overflow: hidden;
	border: 1px solid var(--app-border);
	border-radius: var(--app-radius-md);
	font-size: var(--app-font-sm);
}
.tcs-preview__nav {
	width: 118px;
	flex-shrink: 0;
	padding: 14px 8px;
	color: var(--app-sidebar-text);
	background: var(--app-sidebar-bg);
}
.tcs-preview__nav strong {
	display: block;
	padding: 0 8px 14px;
	font-size: var(--app-font-base);
	letter-spacing: 1px;
}
.tcs-preview__nav > span {
	display: flex;
	align-items: center;
	gap: 7px;
	padding: 9px 8px;
	border-radius: var(--app-radius-sm);
	opacity: 0.75;
}
.tcs-preview__nav .is-selected {
	opacity: 1;
	color: var(--app-sidebar-active);
	background: var(--app-sidebar-hover);
}
.tcs-preview__main {
	flex: 1;
	min-width: 0;
	background: var(--app-page-bg);
}
.tcs-preview__bar {
	padding: 10px 14px;
	background: var(--app-topbar-bg);
	color: var(--app-topbar-text);
}
.tcs-preview__bar b {
	font-weight: 500;
}
.tcs-preview__avatar {
	display: grid;
	place-items: center;
	width: 22px;
	height: 22px;
	border-radius: 50%;
	background: var(--app-topbar-hover);
}
.tcs-preview__content {
	padding: 14px;
}
.tcs-preview__summary {
	padding: 12px;
	border: 1px solid var(--app-border);
	border-radius: var(--app-radius);
	background: var(--app-surface);
}
.tcs-preview__summary > div > span {
	color: var(--app-text-secondary);
}
.tcs-preview__summary strong {
	display: block;
	margin-top: 5px;
	color: var(--app-text);
	font-size: 23px;
}
.tcs-preview__summary small {
	font-size: var(--app-font-sm);
	font-weight: 400;
	color: var(--app-text-secondary);
}
.tcs-preview__button {
	padding: 8px 12px;
	border-radius: var(--app-radius-sm);
	background: var(--app-primary);
	color: var(--app-primary-contrast);
	white-space: nowrap;
}
.tcs-preview__row {
	margin-top: 10px;
	padding: 10px;
	gap: 6px;
	border-radius: var(--app-radius-sm);
	background: var(--app-surface-selected);
	color: var(--app-primary-text);
}
.tcs-preview__tag {
	padding: 2px 6px;
	border: 1px solid var(--app-primary-border);
	border-radius: var(--app-radius-sm);
	white-space: nowrap;
}
.tcs-preview__link {
	white-space: nowrap;
}
.tcs-save-hint {
	align-self: center;
	font-size: var(--app-font-sm);
	color: var(--app-text-secondary);
}
@media (max-width: 540px) {
	.tcs-intro h3 {
		font-size: 16px;
	}
	.tcs-presets {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}
	.tcs-mode {
		padding: 9px;
	}
	.tcs-mode__hint {
		line-height: 1.5;
	}
	.tcs-custom {
		flex-wrap: wrap;
		gap: 8px;
	}
	.tcs-custom__label span {
		display: none;
	}
	.tcs-content {
		padding: 20px 16px;
	}
	.tcs-footer {
		padding-left: 16px;
		padding-right: 16px;
	}
	.tcs-hex {
		width: 86px;
	}
	.tcs-preview__nav {
		width: 82px;
		padding: 12px 5px;
	}
	.tcs-preview__nav strong {
		padding-left: 4px;
		letter-spacing: 0;
	}
	.tcs-preview__nav > span {
		padding: 9px 4px;
		gap: 4px;
	}
	.tcs-preview__content {
		padding: 8px;
	}
	.tcs-preview__summary {
		padding: 9px;
	}
	.tcs-preview__row {
		flex-wrap: wrap;
	}
	.tcs-preview__tag {
		margin-left: auto;
	}
}
@media (prefers-reduced-motion: reduce) {
	.tcs-mode {
		transition: none;
	}
}
</style>

<style>
.el-drawer.tcs-drawer {
	max-width: 100vw;
	background: var(--app-surface);
	color: var(--app-text);
	box-shadow: var(--app-shadow-lg);
}
.tcs-drawer .el-drawer__header {
	margin: 0;
	padding: 20px 24px;
	border-bottom: 1px solid var(--app-border);
	color: var(--app-text);
	font-size: var(--app-font-lg);
	font-weight: 600;
}
.tcs-drawer .el-drawer__body {
	display: flex;
	flex-direction: column;
	min-height: 0;
	overflow: hidden;
}
.tcs-drawer .el-drawer__close-btn {
	color: var(--app-primary-text);
}
.tcs-drawer .el-drawer__close-btn:hover {
	background: var(--app-primary-soft);
}
.tcs-drawer .el-drawer__close-btn:focus-visible {
	outline: 2px solid var(--app-primary-text);
	outline-offset: 2px;
}
</style>
